/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useEffect, useRef, useCallback } from 'react';
import {
  View,
  Animated,
  Dimensions,
  TouchableOpacity,
  Text,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import {
  pd,
  getTextStyle,
  getColor,
} from '@airasia-phoenix/react-native/utils';
import IconView from '@airasia-phoenix/react-native/components/IconView';
import FeedbackIcon from '@move-travel/react-native-phoenix-icons/Feedback';

const { width: screenWidth } = Dimensions.get('window');

export interface ScreenshotOverlayProps {
  screenshotData: string | null;
  isVisible: boolean;
  onPress: () => void;
  onClose: () => void;
}

const ScreenshotOverlay: React.FC<ScreenshotOverlayProps> = ({
  isVisible,
  onClose,
  onPress,
}) => {
  const slideAnim = useRef(new Animated.Value(screenWidth)).current;
  const fadeAnim = useRef(new Animated.Value(0)).current;

  const hideOverlay = useCallback(() => {
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: screenWidth,
        duration: 180,
        useNativeDriver: true,
      }),
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 120,
        useNativeDriver: true,
      }),
    ]).start(() => {
      onClose();
    });
  }, [slideAnim, fadeAnim, onClose]);

  useEffect(() => {
    if (isVisible) {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 0,
          duration: 160,
          useNativeDriver: true,
        }),
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 120,
          useNativeDriver: true,
        }),
      ]).start();

      const timer = setTimeout(() => {
        hideOverlay();
      }, 5000);

      return () => clearTimeout(timer);
    } else {
      slideAnim.setValue(screenWidth);
      fadeAnim.setValue(0);
    }
  }, [isVisible, slideAnim, fadeAnim, hideOverlay]);

  if (!isVisible) {
    return null;
  }

  return (
    <Animated.View style={[styles.overlay, { opacity: fadeAnim }]}>
      <TouchableOpacity
        style={styles.backdrop}
        activeOpacity={1}
        onPress={hideOverlay}
      />
      <Animated.View
        style={[
          styles.menuWrapper,
          {
            transform: [{ translateX: slideAnim }],
          },
        ]}
      >
        <View style={styles.container}>
          <TouchableOpacity
            style={styles.optionRow}
            activeOpacity={0.7}
            onPress={onPress}
          >
            <IconView
              height={20}
              width={20}
              tint={getColor(pd.sys.colour.inline_white)}
            >
              <FeedbackIcon />
            </IconView>
            <Text
              style={getTextStyle(
                pd.sys.typescale.sub_body_text,
                pd.sys.colour.inline_white,
              )}
            >
              Feedback
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.optionRow}
            activeOpacity={0.7}
            onPress={hideOverlay}
          >
            <IconView
              icon={pd.icon.main.navigation.cancel}
              height={20}
              width={20}
              tint={getColor(pd.sys.colour.inline_white)}
            />
            <Text
              style={getTextStyle(
                pd.sys.typescale.sub_body_text,
                pd.sys.colour.inline_white,
              )}
            >
              Cancel
            </Text>
          </TouchableOpacity>
        </View>
      </Animated.View>
    </Animated.View>
  );
};

const styles = StyleSheet.create((pd) => ({
  overlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 9999,
    justifyContent: 'center',
    alignItems: 'flex-end',
  },
  backdrop: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.2)',
  },
  menuWrapper: {
    position: 'absolute',
    right: 16,
    bottom: 100,
  },
  container: {
    backgroundColor: getColor(pd.sys.colour.container_high),
    borderRadius: 12,
    overflow: 'hidden' as const,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.3,
    shadowRadius: 6,
    elevation: 8,
    padding: 8,
    paddingBottom: 16,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    gap: 12,
  },
  optionText: {
    fontWeight: '500',
    fontSize: 15,
    color: '#fff',
  },
}));

export default ScreenshotOverlay;
